import { Context } from "../../context";
import {
  ActorOfInterest,
  DefensivePlayMode,
  DefensivePlayState,
  OffensivePlayState,
  PlayStateManagerCb,
} from "../../managers/playstate";
import {
  CameraLandmark,
  CameraLandmarkLookup,
  CameraMode,
  nearestLandmark,
} from "../controller";
import { ControllerStrategy } from "../strategy";
import { LookAtFriendlyTransitionControllerStrategy } from "../tracking";
import { OfftrackRevertControllerStrategy } from "./action";

/**
 * Abuses the controller strategy pattern by commandeering control of
 * the controller's strategies list - allows for dynamic camera modes
 * based on GameState
 */
export class LandmarkActionControllerStrategy extends LookAtFriendlyTransitionControllerStrategy {
  private stateUpdateCb?: PlayStateManagerCb;

  init(context: Context): ControllerStrategy {
    super.init(context);

    if (!this.registered) {
      console.error("no controller registered for action strategy");
      return this;
    }

    this.registered.addAuxStrategy(OfftrackRevertControllerStrategy);

    return this;
  }

  update(deltaS: number, gameTimeDelta: number, frameNum?: number) {
    if (!this.registered) {
      console.error("no controls registered");
      return;
    }

    super.update(deltaS, gameTimeDelta, frameNum);

    // lazy register for play state updates here because if we do it in init, the callback
    // triggering steps on itself (setMode gets called simultaneously in different threads)
    if (!this.stateUpdateCb) {
      // play state callback
      this.stateUpdateCb = (
        defense: DefensivePlayState,
        _offense: OffensivePlayState
      ) => {
        this.playStateUpdated(defense);
      };

      const playStateManager = this.registered.contextRef.playStateManager;
      playStateManager?.addSubscriber("landmark-cam", this.stateUpdateCb);
    }
  }

  playStateUpdated(state: DefensivePlayState) {
    const controller = this.registered;
    if (!controller) return;

    const playState = state;

    controller.state.positionId =
      playState.posOfInterest.primary instanceof ActorOfInterest
        ? playState.posOfInterest.primary.id
        : 1;

    // TODO: add back under debug flag - useful for debugging state transitions
    // console.log(
    //   "changing action cam game state",
    //   playState.mode,
    //   controller.state.positionId
    // );

    let lookAtActor = true;

    switch (playState.mode) {
      case DefensivePlayMode.BEGIN:
        // NB: set location doesn't work for LOOKAT camera modes
        this.setupTransition(CameraLandmarkLookup[CameraLandmark.HH], 8);
        break;

      case DefensivePlayMode.PITCHING:
        lookAtActor = false;
        // NB: set location doesn't work for LOOKAT camera modes
        this.setupTransition(CameraLandmarkLookup[CameraLandmark.HH]);
        break;

      case DefensivePlayMode.THROWING:
        lookAtActor = false;
      // fallthrough
      case DefensivePlayMode.FIELDING: {
        if (playState.posOfInterest.primary instanceof ActorOfInterest) {
          const actorPos = playState.posOfInterest.primary.position;

          if (actorPos) {
            this.setupTransition(
              CameraLandmarkLookup[nearestLandmark(actorPos)]
            );
          } else {
            console.warn(
              `no actor found for POI ${playState.posOfInterest.primary.position}`
            );
          }
        } else {
          console.error("missing POI in defensive state");
        }
        break;
      }

      default:
        console.error("unsupported game state in action cam");
        break;
    }

    if (!lookAtActor) {
      controller.setMode(CameraMode.LOOK_AT_BALL, true);
    } else {
      controller.setMode(CameraMode.LOOK_AT, true);
    }
  }

  teardown() {
    super.teardown();

    if (!this.registered) return;

    this.registered.removeAuxStrategy(OfftrackRevertControllerStrategy);

    const playStateManager = this.registered.contextRef.playStateManager;
    playStateManager?.removeSubscriber("landmark-cam");
  }
}
